'use client';

import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import dynamic from 'next/dynamic';
import { useSignupCtas } from '@/components/utils/SignupModeProvider';
import { sendGtmEvent } from '@/lib/gtm/gtm';

// Form, calendario e Radix Dialog stanno in un chunk a parte: la landing non
// li scarica finché la demo non serve davvero.
const DemoModal = dynamic(() => import('./DemoModal'), { ssr: false });

export interface DemoModalControls {
  open: (source?: string) => void;
  close: () => void;
  isOpen: boolean;
}

const DemoModalContext = createContext<DemoModalControls>({
  open: () => {},
  close: () => {},
  isOpen: false,
});

/**
 * Ospita il modal di prenotazione demo e lo apre da qualunque CTA del sito.
 * Il modal resta montato dopo la prima apertura, così chi chiude per sbaglio
 * lo riapre al passo in cui era.
 */
const DemoModalProvider = ({ children }: { children: React.ReactNode }) => {
  const { hasDemo } = useSignupCtas();
  const [isOpen, setIsOpen] = useState(false);
  const [hasOpened, setHasOpened] = useState(false);

  const open = useCallback((source = 'cta') => {
    setHasOpened(true);
    setIsOpen(true);
    sendGtmEvent({ event: 'demo_modal_opened', demo_source: source });
  }, []);

  const close = useCallback(() => setIsOpen(false), []);

  const controls = useMemo<DemoModalControls>(
    () => ({ open, close, isOpen }),
    [open, close, isOpen]
  );

  // Con la demo attiva il chunk viene scaricato a pagina ferma: il primo clic
  // apre subito invece di aspettare la rete.
  useEffect(() => {
    if (!hasDemo || hasOpened) return;
    const timer = window.setTimeout(() => {
      void import('./DemoModal');
    }, 2000);
    return () => window.clearTimeout(timer);
  }, [hasDemo, hasOpened]);

  return (
    <DemoModalContext.Provider value={controls}>
      {children}
      {hasOpened && <DemoModal open={isOpen} onOpenChange={setIsOpen} />}
    </DemoModalContext.Provider>
  );
};

/** Apre il modal di prenotazione demo. */
export const useOpenDemoModal = () => useContext(DemoModalContext).open;

/**
 * Comandi e stato del modal. Serve alla pagina /demo, che lo apre da sola e
 * deve sapere quando viene chiuso; altrove basta `useOpenDemoModal()`.
 */
export const useDemoModal = (): DemoModalControls => useContext(DemoModalContext);

export default DemoModalProvider;
